import { Injectable } from '@angular/core';
import { ObjectNameEnum } from '@base/modules/rest/master-data-history/model/enums/object-name.enum';
import { BehaviorSubject, merge, Observable, scan, share, Subject } from 'rxjs';
import { BeyondTabModel } from '@shared/components/beyond-tabs/beyond-tab.model';
import { generateUUID } from '@base/utils/uuid';
import { map } from 'rxjs/operators';
import {
  ObjectViewCurrentSettingsModel
} from '@views/secured/object/components/object/model/object-view-current-settings.model';
import { ObjectCompositeFilterModel } from '@base/modules/rest/object/model/object-composite-filter.model';
import { getActiveApplicationId } from '@base/modules/rest/common/active-application.util';

@Injectable({
  providedIn: 'root',
})
export class BeyondTabsService {
  private _objectToOpen$ = new Subject<{
    objectName: ObjectNameEnum;
    id?: number;
    applicationName?: string;
    applicationId?: number;
    compositeFilter?: ObjectCompositeFilterModel;
    params?: { [key: string]: any };
  }>();
  private _objectToRemove$ = new Subject<string>();
  private _tabDataChanged$ = new Subject<void>();
  private _activeTab$ = new BehaviorSubject<ObjectViewCurrentSettingsModel>(null);

  private addToState = (update: { objectName: ObjectNameEnum; id: number }) =>
    (state: BeyondTabModel[]): BeyondTabModel[] => {
      const beyondTab = this.convertObjectNameToBeyondTab(update);
      return [...state, beyondTab];
    };

  private convertObjectNameToBeyondTab(obj: {
    objectName: ObjectNameEnum;
    id: number;
    applicationName?: string;
    applicationId?: number;
    compositeFilter?: ObjectCompositeFilterModel;
    params?: { [key: string]: any };
  }): BeyondTabModel {
    return {
      id: generateUUID(),
      settings: {
        parent: null,
        objectName: obj.objectName,
        dataId: obj.id,
        applicationName: obj?.applicationName,
        applicationId: obj?.applicationId,
        standalone: true,
        chooser: false,
        compositeFilter: obj.compositeFilter,
        params: obj.params,
      },
    };
  }

  private deleteFromState = (id: string) =>
    (state: BeyondTabModel[]): BeyondTabModel[] => state.filter(item => item.id !== id);

  private scanFn = (state: BeyondTabModel[], fn: (state: BeyondTabModel[]) => BeyondTabModel[]) =>
    fn(state);

  private _objectTabs$ = merge(
    this._objectToOpen$.asObservable().pipe(map(this.addToState)),
    this._objectToRemove$.asObservable().pipe(map(this.deleteFromState))
  ).pipe(scan(this.scanFn, []));

  addTab(payload: {
    objectName: ObjectNameEnum;
    id?: number;
    compositeFilter?: ObjectCompositeFilterModel;
    params?: { [key: string]: any };
  }): void {
    const applicationId = getActiveApplicationId();
    this._objectToOpen$.next({...payload, applicationId});
  }

  get objectTabs$(): Observable<BeyondTabModel[]> {
    return this._objectTabs$
      .pipe(share());
  }

  removeTab(id: string): void {
    this._objectToRemove$.next(id);
  }

  tabDataChanged(): void {
    this._tabDataChanged$.next();
  }

  get tabDataChanged$(): Observable<void> {
    return this._tabDataChanged$;
  }

  activeTabChanged(objectSettings: ObjectViewCurrentSettingsModel): void {
    this._activeTab$.next(objectSettings);
  }

  get activeTab$(): Observable<ObjectViewCurrentSettingsModel> {
    return this._activeTab$.asObservable();
  }

  get activeTab(): ObjectViewCurrentSettingsModel {
    return this._activeTab$.value;
  }
}
